import React, { useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { MAP_H, routeGeom, bezier } from "./TourAmbience";

export function TourAirport({ airport, planes }) {
  const locked = !airport.unlocked;
  return (
    <div data-testid="tour-airport" data-locked={locked ? "true" : "false"} className="absolute z-10 flex flex-col items-center pointer-events-none"
         style={{ left: `${airport.x}%`, top: `${airport.y}%`, width: "5.6%", transform: "translate(-50%,-50%)" }}>
      <div className="relative w-full aspect-square">
        <img src={`/tour/${airport.icon}`} alt="Airport" draggable={false} className="w-full h-full object-contain"
             style={{ filter: locked ? "grayscale(1) brightness(0.45)" : "drop-shadow(0 0 10px #00F0FF)" }} />
        {locked && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="p-1 rounded-full bg-black/80 border border-[#FFE600]/70"><Lock className="w-3 h-3 text-[#FFE600]" /></span>
          </span>
        )}
      </div>
      <div className="mt-1 w-[150%] text-center rounded-md bg-[#0A051A]/70 backdrop-blur-sm border border-white/10 px-1 py-0.5">
        <div className="hidden lg:block font-heading text-[8px] uppercase tracking-wide leading-[1.15] text-white">Airport</div>
        <div className="font-mono text-[7px] leading-tight text-[#A89BCC]" data-testid="tour-airport-status">
          {locked ? `Locked · ${airport.unlock_at} purchases` : `${planes.filter((p) => p.enabled).length} plane(s) active`}
        </div>
      </div>
    </div>
  );
}

// Smooth per-plane phase (0..1): advances in real time and eases toward the server value instead of snapping,
// so short flights (speed upgrades) don't jitter/teleport on every poll.
function usePlanePhases(planes) {
  const target = useRef({});
  const shown = useRef({});
  const [, force] = useState(0);
  useEffect(() => {
    const at = performance.now();
    planes.forEach((p) => { target.current[p.id] = { prog: p.progress, ft: p.flight_time, at }; });
  }, [planes]);
  useEffect(() => {
    let raf, last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000); last = now;
      Object.entries(target.current).forEach(([id, t]) => {
        const want = (((t.prog + (now - t.at) / 1000) % t.ft) + t.ft) % t.ft / t.ft;
        const cur = shown.current[id];
        if (cur === undefined) { shown.current[id] = want; return; }
        let next = (cur + dt / t.ft) % 1;
        let diff = want - next;
        if (diff > 0.5) diff -= 1; else if (diff < -0.5) diff += 1;
        next = Math.abs(diff) > 0.25 ? want : (next + diff * Math.min(1, dt * 3) + 1) % 1;
        shown.current[id] = next;
      });
      force((n) => (n + 1) % 1e6);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return shown.current;
}

export function TourPlanes({ airport, planes, locations }) {
  const phases = usePlanePhases(planes);
  if (!airport?.unlocked) return null;
  const byId = Object.fromEntries((locations || []).map((l) => [l.id, l]));
  return (
    <div className="absolute inset-0 pointer-events-none z-20">
      {planes.filter((p) => p.enabled && byId[p.location_id]).map((p) => {
        const t = phases[p.id] ?? (p.progress % p.flight_time) / p.flight_time;
        const out = t < 0.5;
        const pt = bezier(routeGeom(airport, byId[p.location_id]), out ? t * 2 : 2 - t * 2);
        const deg = (Math.atan2(out ? pt.dy : -pt.dy, out ? pt.dx : -pt.dx) * 180) / Math.PI + 90;
        return (
          <img key={p.id} src="/tour/World Tour Icon Plane.webp" alt="" data-testid={`tour-plane-${p.id}`}
               className="absolute w-[2.4%] aspect-square"
               style={{ left: `${pt.x}%`, top: `${(pt.y / MAP_H) * 100}%`, transform: `translate(-50%,-50%) rotate(${deg}deg)`,
                        filter: "drop-shadow(0 0 6px #00F0FF)" }} />
        );
      })}
    </div>
  );
}
